'use client';

import { HardHat, Monitor } from 'lucide-react';
import { useRouter } from 'next/navigation';

type AccessMode = 'platform' | 'field';

export function AccessModeToggle({
    currentMode,
    className = '',
}: {
    currentMode: AccessMode;
    className?: string;
}) {
    const router = useRouter();

    const modes: Array<{ mode: AccessMode; label: string; icon: typeof Monitor; href: string }> = [
        { mode: 'platform', label: 'Office', icon: Monitor, href: '/' },
        { mode: 'field', label: 'Field', icon: HardHat, href: '/crew' },
    ];

    function handleModeChange(mode: AccessMode, href: string) {
        if (mode === currentMode) return;

        if (mode === 'field') {
            try {
                window.sessionStorage.setItem(
                    'mc:last-platform-path',
                    window.location.pathname + window.location.search + window.location.hash
                );
            } catch {
                // Losing the return path should not block switching modes.
            }
            router.push(href);
            return;
        }

        try {
            router.push(window.sessionStorage.getItem('mc:last-platform-path') || href);
        } catch {
            router.push(href);
        }
    }

    return (
        <div className={`inline-flex rounded-lg border border-zinc-800 bg-zinc-950 p-1 ${className}`} aria-label="Interface mode">
            {modes.map(({ mode, label, icon: Icon, href }) => {
                const active = currentMode === mode;
                return (
                    <button
                        key={mode}
                        type="button"
                        onClick={() => handleModeChange(mode, href)}
                        aria-pressed={active}
                        className={`flex min-h-[34px] flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors ${
                            active
                                ? 'bg-emerald-600 text-white'
                                : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                        }`}
                    >
                        <Icon className="h-4 w-4" />
                        {label}
                    </button>
                );
            })}
        </div>
    );
}
